// catalogue.ts — Phase A audio. Tag-to-file mapping for the three audio
// channels the model can drive via stateDelta: ambient, sporadic, one-shot.
//
// Assets live in `web/public/sounds/` (relative paths in `src` here resolve
// against the deployed `/sounds/` directory at runtime). Target format:
// OGG @ 96kbps mono unless the asset is a stereo bed where stereo matters
// (rain at a window, a forest at night). Aim for a total bundle around
// 5.5 MB so the first-turn audio cost stays small on phones.
//
// Licensing — only CC0 sources. Pixabay Sound and OpenGameArt CC0 are the
// two we're using. Specifically AVOID:
//   - BBC Sound Effects (non-commercial-only)
//   - Mixkit (terms restrict resale / packaged use)
//   - Freesound CC-BY-NC entries
//   - Epidemic Sound / Artlist (subscription, attribution doesn't free them)
//
// Unknown tags `console.warn` and play nothing (see useAudio.ts) so the
// model can coin a fresh tag without breaking the world; the asset can
// land later in a separate commit.
//
// Phase A SHIPS WITH ALL ENTRIES COMMENTED OUT — the system compiles, the
// model is free to emit tags, and the unknown-tag warnings drive the
// curation list. Uncomment each entry as the corresponding file lands.
//
// The full tag menu is enumerated below as commented entries, grouped by
// scenario. See `web/docs/AUDIO_CATALOGUE.md` for the design rationale —
// which sounds each scenario wants, which are Phase A vs Phase B, and the
// sourcing notes. The curation script (`web/scripts/curateAudio.ts`) parses
// these comments to discover what's still uncurated.

export interface AmbientEntry {
  /** Filename under `/sounds/`, e.g. "rain_window.ogg". */
  src: string;
  /** Playback volume 0..1. Default 1.0 when omitted. */
  baseVolume?: number;
}

export interface SporadicEntry {
  src: string;
  /** Minimum delay between plays in ms. */
  minIntervalMs: number;
  /** Maximum delay between plays in ms. Equal to min for regular cadence. */
  maxIntervalMs: number;
  /**
   * Playback volume 0..1. Defaults to 1.1 (slightly louder than ambient) —
   * sporadic plays are punctuation and need to sit ON TOP of the bed.
   */
  baseVolume?: number;
}

export interface OneShotEntry {
  src: string;
  /** Playback volume 0..1. Default 1.0 when omitted. */
  baseVolume?: number;
}

/**
 * Continuous loops keyed by tag. Set via the model's `setAmbient` delta;
 * cleared with `null`. Crossfades between entries take 1s (see useAudio.ts).
 */
export const AMBIENT_CATALOGUE: Record<string, AmbientEntry> = {
  // ── Universal core ────────────────────────────────────────────────────
  rain_window:        { src: "rain_window.ogg" },
  fire_hearth:        { src: "fire_hearth.ogg" },
  wind_eaves:         { src: "wind_eaves.ogg" },
  forest_night:       { src: "forest_night.ogg" },
  forest_day:         { src: "forest_day.ogg" },
  cellar_drip:        { src: "cellar_drip.ogg" },
  wind_howl:          { src: "wind_howl.ogg" },
  crowd_murmur:       { src: "crowd_murmur.ogg" },

  // ── Ember Hollow ─────────────────────────────────────────────────────
  valley_dusk:        { src: "valley_dusk.ogg" },
  library_silence:    { src: "library_silence.ogg" },
  chapel_stone:       { src: "chapel_stone.ogg" },
  garden_overgrown:   { src: "garden_overgrown.ogg" },

  // ── Verity Drift ─────────────────────────────────────────────────────
  ship_hum:           { src: "ship_hum.ogg" },
  recyc_air:          { src: "recyc_air.ogg" },
  arboretum_deck:     { src: "arboretum_deck.ogg" },
  hibernation_bay:    { src: "hibernation_bay.ogg" },

  // ── The Drowned Cathedral ────────────────────────────────────────────
  tide_nave:          { src: "tide_nave.ogg" },
  sea_swell:          { src: "sea_swell.ogg" },
  bell_drone:         { src: "bell_drone.ogg" },

  // ── The Hollow Court ─────────────────────────────────────────────────
  court_silence:      { src: "court_silence.ogg" },
  indoor_winter:      { src: "indoor_winter.ogg" },
  distant_clarion:    { src: "distant_clarion.ogg" },

  // ── The Salt-Glass Tower ─────────────────────────────────────────────
  desert_wind:        { src: "desert_wind.ogg" },
  lens_hum:           { src: "lens_hum.ogg" },
  dome_silence:       { src: "dome_silence.ogg" },

  // ── The Watchman's Lantern ───────────────────────────────────────────
  train_carriage:     { src: "train_carriage.ogg" },
  dining_car:         { src: "dining_car.ogg" },
  locomotive_idle:    { src: "locomotive_idle.ogg" },

  // ── Veridian Bloom ───────────────────────────────────────────────────
  sim_morning:        { src: "sim_morning.ogg" },
  maintenance_layer:  { src: "maintenance_layer.ogg" },
  control_plane:      { src: "control_plane.ogg" },
  rawworld_street:    { src: "rawworld_street.ogg" },

  // ── The Greenway ─────────────────────────────────────────────────────
  inn_common_room:    { src: "inn_common_room.ogg" },
  open_moor:          { src: "open_moor.ogg" },
  river_ford:         { src: "river_ford.ogg" },

  // ── Vaultsworn ───────────────────────────────────────────────────────
  torch_corridor:     { src: "torch_corridor.ogg" },
  company_camp:       { src: "company_camp.ogg" },
  drowned_chapel:     { src: "drowned_chapel.ogg" },

  // ── The Coldwater Line ───────────────────────────────────────────────
  safehouse_morning:  { src: "safehouse_morning.ogg" },
  hotel_corridor:     { src: "hotel_corridor.ogg" },
  ferry_fog:          { src: "ferry_fog.ogg" },

  // ── The Sleeping Floor ───────────────────────────────────────────────
  hotel_lobby:        { src: "hotel_lobby.ogg" },
  dream_city:         { src: "dream_city.ogg" },
  limbo_silence:      { src: "limbo_silence.ogg" },

  // ── Saucer Country ──────────────────────────────────────────────────
  desert_dusk:        { src: "desert_dusk.ogg" },
  motel_neon_hum:     { src: "motel_neon_hum.ogg" },
  crowd_anxious:      { src: "crowd_anxious.ogg" },
  diner_counter:      { src: "diner_counter.ogg" },
  // radio_static:       { src: "radio_static.ogg" },
  saucer_hum:         { src: "saucer_hum.ogg" },
};

/**
 * Recurring punctuation keyed by tag. Set via the model's `setSporadic`
 * delta; cleared with `null`. The scheduler picks a random delay in
 * [minIntervalMs, maxIntervalMs], plays once, then reschedules. Equal
 * min/max gives a regular cadence (clock tick, tap drip).
 */
export const SPORADIC_CATALOGUE: Record<string, SporadicEntry> = {
  // ── Universal core ────────────────────────────────────────────────────
  // owl_hoots:          { src: "owl_hoots.ogg",       minIntervalMs: 25000, maxIntervalMs: 60000 },
  // water_drip:         { src: "drip.ogg",            minIntervalMs:  3000, maxIntervalMs:  8000 },
  // tap_drip:           { src: "drip.ogg",            minIntervalMs:  4500, maxIntervalMs:  4500 },
  // distant_thunder:    { src: "distant_thunder.ogg", minIntervalMs: 40000, maxIntervalMs: 90000 },
  // clock_tick:         { src: "clock_tick.ogg",      minIntervalMs:  1000, maxIntervalMs:  1000 },
  // floorboard_creak:   { src: "floorboard_creak.ogg",minIntervalMs: 18000, maxIntervalMs: 45000 },
  // wind_gust:          { src: "wind_gust.ogg",       minIntervalMs: 20000, maxIntervalMs: 50000 },
  // bird_distant:       { src: "bird_distant.ogg",    minIntervalMs: 15000, maxIntervalMs: 40000 },

  // ── Ember Hollow ─────────────────────────────────────────────────────
  // rook_caw:           { src: "rook_caw.ogg",        minIntervalMs: 25000, maxIntervalMs: 70000 },
  // distant_bell:       { src: "distant_bell.ogg",    minIntervalMs: 60000, maxIntervalMs: 180000 },
  // ivy_rustle:         { src: "ivy_rustle.ogg",      minIntervalMs: 20000, maxIntervalMs: 55000 },

  // ── Verity Drift ─────────────────────────────────────────────────────
  // pressure_door:      { src: "pressure_door.ogg",   minIntervalMs: 35000, maxIntervalMs: 90000 },
  // intercom_blip:      { src: "intercom_blip.ogg",   minIntervalMs: 45000, maxIntervalMs: 120000 },
  // lights_wake:        { src: "lights_wake.ogg",     minIntervalMs: 30000, maxIntervalMs: 80000 },

  // ── The Drowned Cathedral ────────────────────────────────────────────
  // rope_drip:          { src: "rope_drip.ogg",       minIntervalMs:  6000, maxIntervalMs: 14000 },
  // bell_buoy:          { src: "bell_buoy.ogg",       minIntervalMs: 25000, maxIntervalMs: 60000 },
  // gull_cry:           { src: "gull_cry.ogg",        minIntervalMs: 18000, maxIntervalMs: 45000 },

  // ── The Hollow Court ─────────────────────────────────────────────────
  // claw_click:         { src: "claw_click.ogg",      minIntervalMs: 12000, maxIntervalMs: 35000 },
  // silk_rustle:        { src: "silk_rustle.ogg",     minIntervalMs: 20000, maxIntervalMs: 50000 },
  // distant_hunt_horn:  { src: "distant_hunt_horn.ogg", minIntervalMs: 60000, maxIntervalMs: 180000 },

  // ── The Salt-Glass Tower ─────────────────────────────────────────────
  // salt_crack:         { src: "salt_crack.ogg",      minIntervalMs: 18000, maxIntervalMs: 45000 },
  // brass_dial:         { src: "brass_dial.ogg",      minIntervalMs: 30000, maxIntervalMs: 70000 },
  // lens_chime:         { src: "lens_chime.ogg",      minIntervalMs: 22000, maxIntervalMs: 55000 },

  // ── The Watchman's Lantern ───────────────────────────────────────────
  // pocketwatch_tick:   { src: "pocketwatch_tick.ogg",minIntervalMs:  1000, maxIntervalMs:  1000 },
  // train_whistle:      { src: "train_whistle.ogg",   minIntervalMs: 90000, maxIntervalMs: 240000 },
  // coupling_creak:     { src: "coupling_creak.ogg",  minIntervalMs: 18000, maxIntervalMs: 45000 },

  // ── Veridian Bloom ───────────────────────────────────────────────────
  // crossing_signal:    { src: "crossing_signal.ogg", minIntervalMs: 15000, maxIntervalMs: 30000 },
  // terminal_beep:      { src: "terminal_beep.ogg",   minIntervalMs:  8000, maxIntervalMs: 20000 },
  // sodium_buzz:        { src: "sodium_buzz.ogg",     minIntervalMs: 40000, maxIntervalMs: 90000 },

  // ── The Greenway ─────────────────────────────────────────────────────
  // harness_chime:      { src: "harness_chime.ogg",   minIntervalMs: 18000, maxIntervalMs: 45000 },
  // distant_horn:       { src: "distant_horn.ogg",    minIntervalMs: 60000, maxIntervalMs: 180000 },
  // moor_curlew:        { src: "moor_curlew.ogg",     minIntervalMs: 30000, maxIntervalMs: 90000 },

  // ── Vaultsworn ───────────────────────────────────────────────────────
  // torch_pop:          { src: "torch_pop.ogg",       minIntervalMs: 20000, maxIntervalMs: 60000 },
  // plaster_fall:       { src: "plaster_fall.ogg",    minIntervalMs: 30000, maxIntervalMs: 90000 },
  // distant_drip:       { src: "distant_drip.ogg",    minIntervalMs:  9000, maxIntervalMs: 18000 },

  // ── The Coldwater Line ───────────────────────────────────────────────
  // tram_distant:       { src: "tram_distant.ogg",    minIntervalMs: 35000, maxIntervalMs: 90000 },
  // typewriter_burst:   { src: "typewriter_burst.ogg",minIntervalMs: 12000, maxIntervalMs: 30000 },
  // streetlamp_flicker: { src: "streetlamp_flicker.ogg", minIntervalMs: 25000, maxIntervalMs: 60000 },

  // ── The Sleeping Floor ───────────────────────────────────────────────
  // lift_chime:         { src: "lift_chime.ogg",      minIntervalMs: 25000, maxIntervalMs: 70000 },
  // clock_disagree:     { src: "clock_disagree.ogg",  minIntervalMs: 15000, maxIntervalMs: 40000 },
  // footfall_long:      { src: "footfall_long.ogg",   minIntervalMs: 18000, maxIntervalMs: 45000 },

  // ── Saucer Country ──────────────────────────────────────────────────
  // alien_giggle:       { src: "alien_giggle.ogg",     minIntervalMs: 15000, maxIntervalMs: 40000 },
  // coyote_distant:     { src: "coyote_distant.ogg",   minIntervalMs: 30000, maxIntervalMs: 90000 },
  // drone_chime:        { src: "drone_chime.ogg",      minIntervalMs: 25000, maxIntervalMs: 60000 },
  // helicopter_pass:    { src: "helicopter_pass.ogg",  minIntervalMs: 60000, maxIntervalMs: 180000 },
};

/**
 * Fire-and-forget single plays keyed by tag. Set via the model's `playSfx`
 * delta. Transient — the engine never persists this; useAudio.ts fires the
 * sound exactly once per turn the tag arrives non-null.
 */
export const ONESHOT_CATALOGUE: Record<string, OneShotEntry> = {
  // ── Universal core ────────────────────────────────────────────────────
  door_creak:         { src: "door_creak.ogg" },
  door_close:         { src: "door_close.ogg" },
  glass_smash:        { src: "glass_smash.ogg" },
  footsteps_stairs:   { src: "footsteps_stairs.ogg" },
  footsteps_wood:     { src: "footsteps_wood.ogg" },
  footsteps_stone:    { src: "footsteps_stone.ogg" },
  key_turn:           { src: "key_turn.ogg" },
  latch_click:        { src: "latch_click.ogg" },
  page_turn:          { src: "page_turn.ogg" },
  match_strike:       { src: "match_strike.ogg" },
  candle_blow:        { src: "candle_blow.ogg" },
  heartbeat_one:      { src: "heartbeat_one.ogg" },

  // ── Ember Hollow ─────────────────────────────────────────────────────
  iron_gate:          { src: "iron_gate.ogg" },
  grimoire_open:      { src: "grimoire_open.ogg" },
  wax_seal_break:     { src: "wax_seal_break.ogg" },
  // lantern_light:      { src: "lantern_light.ogg" },
  chest_open:         { src: "chest_open.ogg" },

  // ── Verity Drift ─────────────────────────────────────────────────────
  // keycard_swipe:      { src: "keycard_swipe.ogg" },
  bulkhead_seal:      { src: "bulkhead_seal.ogg" },
  console_boot:       { src: "console_boot.ogg" },
  intercom_open:      { src: "intercom_open.ogg" },
  airlock_cycle:      { src: "airlock_cycle.ogg" },

  // ── The Drowned Cathedral ────────────────────────────────────────────
  // censer_swing:       { src: "censer_swing.ogg" },
  bell_strike:        { src: "bell_strike.ogg" },
  // water_step:         { src: "water_step.ogg" },
  breviary_close:     { src: "breviary_close.ogg" },
  font_pour:          { src: "font_pour.ogg" },

  // ── The Hollow Court ─────────────────────────────────────────────────
  // glove_drop:         { src: "glove_drop.ogg" },
  glass_ring:         { src: "glass_ring.ogg" },
  // card_laid:          { src: "card_laid.ogg" },
  // bow_step:           { src: "bow_step.ogg" },
  ink_quill:          { src: "ink_quill.ogg" },

  // ── The Salt-Glass Tower ─────────────────────────────────────────────
  // valve_turn:         { src: "valve_turn.ogg" },
  // lens_set:           { src: "lens_set.ogg" },
  // brass_dial_step:    { src: "brass_dial_step.ogg" },
  chart_unfurl:       { src: "chart_unfurl.ogg" },
  dust_pour:          { src: "dust_pour.ogg" },

  // ── The Watchman's Lantern ───────────────────────────────────────────
  // ticket_stamp:       { src: "ticket_stamp.ogg" },
  // pocketwatch_open:   { src: "pocketwatch_open.ogg" },
  compartment_door:   { src: "compartment_door.ogg" },
  piano_chord:        { src: "piano_chord.ogg" },
  steam_release:      { src: "steam_release.ogg" },

  // ── Veridian Bloom ───────────────────────────────────────────────────
  coin_drop_wrong:    { src: "coin_drop_wrong.ogg" },
  auth_chime:         { src: "auth_chime.ogg" },
  auth_fail:          { src: "auth_fail.ogg" },
  glitch_blip:        { src: "glitch_blip.ogg" },
  layer_shift:        { src: "layer_shift.ogg" },

  // ── The Greenway ─────────────────────────────────────────────────────
  staff_strike:       { src: "staff_strike.ogg" },
  sword_draw:         { src: "sword_draw.ogg" },
  saddle_creak:       { src: "saddle_creak.ogg" },
  cup_set_down:       { src: "cup_set_down.ogg" },
  fire_kindle:        { src: "fire_kindle.ogg" },

  // ── Vaultsworn ───────────────────────────────────────────────────────
  rope_descent:       { src: "rope_descent.ogg" },
  piton_hammer:       { src: "piton_hammer.ogg" },
  sigil_activate:     { src: "sigil_activate.ogg" },
  stone_door:         { src: "stone_door.ogg" },
  chainmail_step:     { src: "chainmail_step.ogg" },

  // ── The Coldwater Line ───────────────────────────────────────────────
  dossier_open:       { src: "dossier_open.ogg" },
  pistol_slide:       { src: "pistol_slide.ogg" },
  lighter_click:      { src: "lighter_click.ogg" },
  pen_uncap:          { src: "pen_uncap.ogg" },
  tram_door:          { src: "tram_door.ogg" },

  // ── The Sleeping Floor ───────────────────────────────────────────────
  totem_spin:         { src: "totem_spin.ogg" },
  mirror_pass:        { src: "mirror_pass.ogg" },
  bell_concierge:     { src: "bell_concierge.ogg" },
  lift_door:          { src: "lift_door.ogg" },
  kick_fall:          { src: "kick_fall.ogg" },

  // ── Saucer Country ──────────────────────────────────────────────────
  disintegration_ray: { src: "disintegration_ray.ogg" },
  translator_chime:   { src: "translator_chime.ogg" },
  saucer_landing:     { src: "saucer_landing.ogg" },
  dove_vaporise:      { src: "dove_vaporise.ogg" },
  rotary_dial:        { src: "rotary_dial.ogg" },
  slot_machine_bells: { src: "slot_machine_bells.ogg" },
  // rc_cola_pop:        { src: "rc_cola_pop.ogg" },
  truck_start:        { src: "truck_start.ogg" },
};
